import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import { CommentRegular, BugRegular, LightbulbRegular } from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const MessageSquare = fluentIcon(CommentRegular);
const Bug = fluentIcon(BugRegular);
const Lightbulb = fluentIcon(LightbulbRegular);

interface Feedback {
  id: string;
  user_id: string | null;
  category: "bug" | "suggestion" | "other";
  message: string;
  app_version: string | null;
  created_at: string;
}

interface FeedbackResponse {
  data: Feedback[];
  total: number;
}

function CategoryIcon({ cat }: { cat: string }) {
  if (cat === "bug") return <Bug size={14} className="text-red-400" />;
  if (cat === "suggestion") return <Lightbulb size={14} className="text-amber-400" />;
  return <MessageSquare size={14} className="text-slate-400" />;
}

export default function FeedbackPage() {
  const { token } = useAuth();
  const { data, isLoading, error } = useQuery<FeedbackResponse>({
    queryKey: ["feedback"],
    queryFn: () => adminGet<FeedbackResponse>("/api/v1/admin/feedback", token),
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold">User Feedback</h1>
        <p className="text-sm text-slate-500 mt-1">Bug reports, suggestions, and other feedback from users.</p>
      </div>

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {error && <div className="card text-red-400">{(error as Error).message}</div>}

      {data && (
        <>
          <div className="mb-4 text-sm text-slate-500">
            {data.total.toLocaleString()} total feedback entries
          </div>
          <div className="space-y-3">
            {data.data.length === 0 && (
              <div className="card text-center text-slate-500 py-12">No feedback yet.</div>
            )}
            {data.data.map((f) => (
              <div key={f.id} className="card card-hover">
                <div className="flex items-start gap-3">
                  <div className="mt-1"><CategoryIcon cat={f.category} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      <span className="px-1.5 py-0.5 bg-slate-800 rounded text-[10px] uppercase">
                        {f.category}
                      </span>
                      <span>{new Date(f.created_at).toLocaleString()}</span>
                      {f.app_version && <span className="font-mono">v{f.app_version}</span>}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">{f.message}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
